import { Injectable } from '@angular/core';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: TurnstileRenderOptions) => string;
      execute: (container: string | HTMLElement, options?: TurnstileRenderOptions) => void;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

export interface TurnstileRenderOptions {
  sitekey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  /** Tiene que coincidir exacto con la action que valida el backend para este endpoint (ej. 'login', 'joinus'). */
  action?: string;
  /** 'execute' pospone la validación hasta llamar a execute() en vez de arrancar sola al montar el widget. */
  execution?: 'render' | 'execute';
  /** 'interaction-only' no ocupa espacio ni se muestra salvo que Cloudflare necesite que el usuario resuelva un challenge. */
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Carga el script de Cloudflare Turnstile una única vez (bajo demanda, no en index.html)
 * y expone wrappers finos sobre `window.turnstile`.
 */
@Injectable({ providedIn: 'root' })
export class TurnstileService {
  private scriptLoadPromise: Promise<void> | null = null;

  private loadScript(): Promise<void> {
    if (!this.scriptLoadPromise) {
      this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
        if (window.turnstile) {
          resolve();
          return;
        }

        const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('No se pudo cargar Cloudflare Turnstile')));
          return;
        }

        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('No se pudo cargar Cloudflare Turnstile'));
        document.head.appendChild(script);
      });
    }

    return this.scriptLoadPromise;
  }

  async render(container: HTMLElement, options: TurnstileRenderOptions): Promise<string> {
    await this.loadScript();
    return window.turnstile!.render(container, options);
  }

  execute(widgetId: string): void {
    window.turnstile?.execute(widgetId);
  }

  reset(widgetId?: string): void {
    window.turnstile?.reset(widgetId);
  }

  remove(widgetId?: string): void {
    window.turnstile?.remove(widgetId);
  }
}
